import { memo, useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Iconify } from "@repo/design-system/components/ui";
import './DropdownActions.scss';
import { DEFAULT_ACTIONS } from "./utils/DefaultAction";

const DropdownComponent = ({ rowData, config = {}, actionHandlers = {} }) => {
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

    useEffect(() => {
        const handleOutsideClick = (event) => {
            const clickedButton = buttonRef.current?.contains(event.target);
            const clickedMenu = menuRef.current?.contains(event.target);
            if (!clickedButton && !clickedMenu) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    useEffect(() => {
        if (!isMenuOpen) return;
        const updatePosition = () => {
            if (buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setMenuPos({
                    top: rect.bottom + 6,
                    right: window.innerWidth - rect.right,
                });
            }
        };
        window.addEventListener("scroll", updatePosition, true);
        return () => window.removeEventListener("scroll", updatePosition, true);
    }, [isMenuOpen]);

    const menuActions = useMemo(() => {
        const actions = [];
        Object.entries(config?.defaultActions || {}).forEach(([key, enabled]) => {
            if (enabled && DEFAULT_ACTIONS[key]) {
                actions.push({ ...DEFAULT_ACTIONS[key], actionType: key });
            }
        });
        if (Array.isArray(config?.customActions)) {
            config.customActions.forEach((action) => {
                actions.push({ ...action, actionType: action?.actionType });
            });
        }
        return actions;
    }, [config]);

    const handleActionClick = (e, actionType) => {
        e?.stopPropagation?.();
        actionHandlers?.[actionType]?.(rowData);
        setIsMenuOpen(false);
    };

    const handleToggle = (e) => {
        e?.stopPropagation?.();
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPos({
                top: rect.bottom + 6,
                right: window.innerWidth - rect.right,
            });
        }
        setIsMenuOpen((prev) => !prev);
    };

    return (
        <div className="action-dropdown-wrapper">
            <button
                ref={buttonRef}
                type="button"
                className="action-dropdown-button"
                onClick={handleToggle}
            >
                <Iconify icon="mdi:dots-vertical" width={18} height={18} />
            </button>

            {isMenuOpen && createPortal(
                <div
                    ref={menuRef}
                    className="action-dropdown-menu"
                    style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "unset" }}
                >
                    {menuActions.map((action, index) => (
                        <button
                            key={`${action?.actionType}-${index}`}
                            type="button"
                            className={`action-dropdown-item${action?.className ? ` ${action.className}` : ""}`}
                            onClick={(e) => handleActionClick(e, action?.actionType)}
                        >
                            <Iconify
                                icon={action?.icon}
                                ttLabel={action.ttLabel}
                                targetId={`${action?.actionType}-${index}`}
                                width={action?.iconWidth || 16}
                                height={action?.iconHeight || 16}
                            />
                            {action?.label}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

export const DropdownActions = memo(DropdownComponent);
